import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import React from 'react';
import type { Edge } from 'react-native-safe-area-context';
import {
    CheckIcon,
    Icon,
    isWeb,
    SafeAreaView,
    ScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { transformBillFromServer } from '../../../common/utils/billUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useUpdateBill, { type UpdateBillBody } from '../../hooks/useUpdateBill';
import {
    activeBillsAtom,
    allBillsAtom,
    billsToReviewAtom,
    selectedBillAtom,
    selectedReviewBillAtom,
} from '../../state/BillsAtoms';
import {
    type _Bill,
    BillContext,
    BillEditTypeParam,
    type IBill,
    type ManualBill,
    type PartialBill,
} from '../../types/Bill';
import BillEditAccount from '../common/BillEditAccount';
import BillEditCategory from '../common/BillEditCategory';
import BillEditFrequency from '../common/BillEditFrequency';
import BillEditLastPaid from '../common/BillEditLastPaid';
import BillDetailsEditHeader from './BillDetailsEditHeader';

export interface BillDetailsEditTypeProps {
    bill: IBill | PartialBill | ManualBill;
    onChange: (bill: IBill | PartialBill | ManualBill) => void;
}

interface BillDetailsEditContainerProps {
    context?: BillContext;
}

const BillDetailsEditContainer = ({
    context,
}: BillDetailsEditContainerProps) => {
    LogRocketFacade.tagPage('BillDetailsEdit');
    const { type } = useLocalSearchParams<{ type: BillEditTypeParam }>();

    const user = useAuthedUser();
    const toast = useToastController();

    const { mutate: updateBill } = useUpdateBill();
    const storeActiveBills = useSetAtom(activeBillsAtom);
    const storeAllBills = useSetAtom(allBillsAtom);
    const storeReviewBills = useSetAtom(billsToReviewAtom);

    const [selectedBill, setSelectedBill] = useAtom(
        context === BillContext.REVIEW
            ? selectedReviewBillAtom
            : selectedBillAtom,
    );

    if (!selectedBill) {
        return <Redirect href="/bills/bills-overview" />;
    }

    const storeBill = (transformedBill: _Bill) => {
        storeActiveBills((prev) => {
            return prev.map((pb) => {
                return pb.id === transformedBill.id ? transformedBill : pb;
            });
        });
        storeAllBills((prev) => {
            return prev.map((pb) => {
                return pb.id === transformedBill.id ? transformedBill : pb;
            });
        });
        if (context === BillContext.REVIEW) {
            storeReviewBills((prev) => {
                return prev.map((pb) => {
                    return pb.id === transformedBill.id ? transformedBill : pb;
                });
            });
        }
    };

    const onChange = (bill: IBill | PartialBill) => {
        const {
            id,
            accountId,
            displayName,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
            memberMarkedAsSwitched,
        } = bill as _Bill;

        updateBill(
            {
                urlParams: {
                    id,
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    accountId,
                    displayName,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                    memberMarkedAsSwitched,
                } satisfies UpdateBillBody,
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = transformBillFromServer(
                        updatedBillFromServer,
                    );
                    storeBill(transformedBill);

                    toast.show(`${payee} bill updated`, {
                        icon: (
                            <View
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="$green1000"
                                borderRadius={12}
                                padding={9}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={24}
                                    color="$white"
                                />
                            </View>
                        ),
                    });
                    setSelectedBill(transformedBill);
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
                onSettled: () => {
                    // delaying the back action briefly so there's visual feedback that a value was selected
                    setTimeout(() => {
                        router.back();
                    }, 100);
                },
            },
        );
    };

    const getEditComponent = () => {
        let componentTitle = '';
        let component: React.JSX.Element | null = null;

        switch (type) {
            case BillEditTypeParam.ACCOUNT:
                component = (
                    <BillEditAccount bill={selectedBill} onChange={onChange} />
                );
                componentTitle = 'Account';
                break;
            case BillEditTypeParam.FREQUENCY:
                component = (
                    <BillEditFrequency
                        bill={selectedBill}
                        onChange={onChange}
                    />
                );
                componentTitle = 'Frequency';
                break;
            case BillEditTypeParam.CATEGORY:
                component = (
                    <BillEditCategory bill={selectedBill} onChange={onChange} />
                );
                componentTitle = 'Category';
                break;
            case BillEditTypeParam.LAST_PAYMENT_DATE:
                component = (
                    <BillEditLastPaid bill={selectedBill} onChange={onChange} />
                );
                componentTitle = 'Last paid';
                break;
            default:
                return null;
        }

        return (
            <SafeAreaView
                edges={[
                    'right',
                    'bottom',
                    'left',
                    ...(context === BillContext.REVIEW ? ['top' as Edge] : []),
                ]}
            >
                <YStack
                    flex={1}
                    padding={20}
                    maxWidth={700}
                    alignSelf="center"
                    width="100%"
                    gap={12}
                    paddingTop={
                        isWeb ? 24 : context === BillContext.REVIEW ? 0 : 20
                    }
                >
                    <BillDetailsEditHeader title={componentTitle} />
                    <ScrollView>
                        <YStack>{component}</YStack>
                    </ScrollView>
                </YStack>
            </SafeAreaView>
        );
    };

    return <>{getEditComponent()}</>;
};

export default BillDetailsEditContainer;
